import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectMapSelectedChitalishte,
  setMapSelectedChitalishte,
} from '../../store/slices/chitalishtaSlice';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import './Karta.css';

const Karta = () => {
  // console.log('Karta component rendered');
  const mapContainerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState('Инициализация...');
  const mapInitializedRef = useRef(false);
  const scriptsLoadedRef = useRef(false);
  const pulsatingLayerRef = useRef(null);
  const postRenderListener = useRef(null);

  const dispatch = useDispatch();
  const selectedChitalishte = useSelector(selectMapSelectedChitalishte);
  // console.log('selectedChitalishte from useSelector:', selectedChitalishte);

  useEffect(() => {

    // Clear previous pulsating point if a new chitalishte is not selected or component unmounts
    if (pulsatingLayerRef.current) {
      pulsatingLayerRef.current.setSource(null);
      if (postRenderListener.current) {
        // @ts-ignore
        ol.Observable.unByKey(postRenderListener.current);
        postRenderListener.current = null;
      }
    }

    if (window.map && selectedChitalishte) {
      const regNumber = selectedChitalishte.slug
        ? selectedChitalishte.slug.split('/')[0]
        : selectedChitalishte.registrationNumber
        ? String(selectedChitalishte.registrationNumber)
        : selectedChitalishte.id
        ? String(selectedChitalishte.id)
        : null;

      // // console.log('Extracted regNumber:', regNumber);

      if (!regNumber) {
        console.error('Could not determine registration number from selected chitalishte:', selectedChitalishte);
        return;
      }

      // @ts-ignore
      const chitalishtaLayer = window.lyr__2;
      if (!chitalishtaLayer) {
          console.error('Chitalishta layer (lyr__2) not found on window object.');
          return;
      }

      // @ts-ignore
      const clusterSource = chitalishtaLayer.getSource();
      if (!clusterSource || typeof clusterSource.getSource !== 'function') {
          console.error('Cluster source not found on the layer.');
          return;
      }

      const vectorSource = clusterSource.getSource();
      if (!vectorSource) {
          console.error('Vector source not found inside the cluster source.');
          return;
      }

      const features = vectorSource.getFeatures();

      const targetFeature = features.find(feature => {
        const link = feature.get('Връзка');
        // The link property is like: "karta.narodnichitalishta.bg/community-center/475/tutrakan/hristo-botev-1942g"
        // We match it with the registrationNumber from the search result.
        const matchString = `/community-center/${regNumber}/`;
        const slugMatch = selectedChitalishte.slug && link && link.includes(selectedChitalishte.slug);
        return slugMatch || (link && link.includes(matchString));
      });

      if (targetFeature) {
        // console.log('Found target feature:', targetFeature.getProperties());
        const geometry = targetFeature.getGeometry();
        if (geometry) {
          const coords = geometry.getCoordinates();
          // console.log('Coordinates found:', coords);

          window.map.getView().animate({
            center: coords,
            zoom: 15,
            duration: 1000,
          });

          // @ts-ignore
          const pulsatingFeature = new ol.Feature(new ol.geom.Point(coords));
          // @ts-ignore
          const pulsatingSource = new ol.source.Vector({
            features: [pulsatingFeature],
          });

          if (!pulsatingLayerRef.current) {
            // @ts-ignore
            pulsatingLayerRef.current = new ol.layer.Vector({
              source: pulsatingSource,
              zIndex: 1000,
            });
            window.map.addLayer(pulsatingLayerRef.current);
          } else {
            pulsatingLayerRef.current.setSource(pulsatingSource);
          }

          const duration = 2000;
          let start = new Date().getTime();

          // @ts-ignore
          const circle = new ol.style.Circle({
            radius: 10,
            fill: new ol.style.Fill({ color: 'rgba(255, 0, 0, 0.8)' }),
            stroke: new ol.style.Stroke({ color: 'red', width: 2 }),
          });

          // @ts-ignore
          const style = new ol.style.Style({
            image: circle,
          });

          pulsatingFeature.setStyle(style);

          if (postRenderListener.current) {
            // @ts-ignore
            ol.Observable.unByKey(postRenderListener.current);
          }

          postRenderListener.current = window.map.on('postrender', (event) => {
            const frameState = event.frameState;
            let elapsed = frameState.time - start;

            if (elapsed > duration) {
              start = frameState.time;
              elapsed = 0;
            }

            const elapsedRatio = elapsed / duration;
            // @ts-ignore
            const radius = ol.easing.easeOut(elapsedRatio) * 20 + 5;
            // @ts-ignore
            const opacity = ol.easing.easeOut(1 - elapsedRatio);

            circle.setRadius(radius);
            // @ts-ignore
            style.getImage().getFill().setColor(`rgba(255, 0, 0, ${opacity})`);
            // @ts-ignore
            style.getImage().getStroke().setColor(`rgba(255, 0, 0, ${opacity})`);

            window.map.render();
          });
        } else {
            console.error('Feature found, but it has no geometry.');
        }
      } else {
        console.warn(`Could not find a feature on the map with registration number: ${regNumber}`);
      }
    }
  }, [selectedChitalishte]);

  useEffect(() => {
    // Prevent double initialization in strict mode
    if (mapInitializedRef.current) {
      setIsLoading(false);
      return;
    }

    const loadScript = (src) => {
      return new Promise((resolve, reject) => {
        // Check if script already exists
        const existingScript = document.querySelector(`script[src="${src}"]`);
        if (existingScript) {
          // console.log(`✓ Скриптът ${src} вече е зареден`);
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.async = false;
        
        script.onload = () => {
          // console.log(`✅ Зареден: ${src}`);
          resolve();
        };
        
        script.onerror = () => {
          // console.error(`❌ Грешка при зареждане: ${src}`);
          reject(new Error(`Не може да се зареди ${src}`));
        };
        
        document.body.appendChild(script);
      });
    };

    const initializeMap = async () => {
      // Ensure map container exists
      if (!mapContainerRef.current) {
        setError('Контейнерът на картата не е намерен');
        setIsLoading(false);
        return;
      }

      try {
        // Load scripts only once
        if (!scriptsLoadedRef.current) {
          const scripts = [
            // Core libraries
            `${process.env.PUBLIC_URL}/resources/qgis2web_expressions.js`,
            `${process.env.PUBLIC_URL}/resources/functions.js`,
            `${process.env.PUBLIC_URL}/resources/ol.js`,
            `${process.env.PUBLIC_URL}/resources/ol-layerswitcher.js`,
            `${process.env.PUBLIC_URL}/resources/photon-geocoder-autocomplete.min.js`,
            
            // Data layers - ВАЖНО: Всички layer файлове преди styles
            `${process.env.PUBLIC_URL}/layers/_1.js`,
            `${process.env.PUBLIC_URL}/layers/_2.js`,  // ⚠️ ДОБАВЕН _2.js
            
            // Styles - след data layers
            `${process.env.PUBLIC_URL}/styles/_1_style.js`,
            `${process.env.PUBLIC_URL}/styles/_2_style.js`,  // ⚠️ ДОБАВЕН _2_style.js
            
            // Layer definitions - след всички layer и style файлове
            `${process.env.PUBLIC_URL}/layers/layers.js?v=2`,
            
            // Utils
            `${process.env.PUBLIC_URL}/resources/Autolinker.min.js`,
          ];

          for (let i = 0; i < scripts.length; i++) {
            setLoadingStatus(`Зареждане ${i + 1}/${scripts.length}...`);
            await loadScript(scripts[i]);
          }

          scriptsLoadedRef.current = true;
        }

        // Now load the main map initialization script
        setLoadingStatus('Инициализация на картата...');
        await loadScript(`${process.env.PUBLIC_URL}/resources/qgis2web.js`);

        // Wait for map initialization
        let attempts = 0;
        const maxAttempts = 50;
        
        const checkMapInitialization = () => {
          return new Promise((resolve, reject) => {
            const checkInterval = setInterval(() => {
              attempts++;

              if (window.map) {
                clearInterval(checkInterval);
                // console.log('✅ Картата е инициализирана успешно');
                
                // Set the map target to our container
                window.map.setTarget(mapContainerRef.current);
                
                // Force map to update its size
                setTimeout(() => {
                  if (window.map) {
                    window.map.updateSize();
                  }
                }, 100);

                mapInitializedRef.current = true;
                resolve();
              } else if (attempts >= maxAttempts) {
                clearInterval(checkInterval);
                reject(new Error('Timeout при инициализация на картата'));
              }
            }, 100);
          });
        };

        await checkMapInitialization();
        
        setIsLoading(false);
        setError(null);

      } catch (err) {
        console.error('❌ Грешка при инициализация на картата:', err);
        setError(err.message || 'Грешка при зареждане на картата');
        setIsLoading(false);
      }
    };

    initializeMap();

    // Cleanup function
    return () => {
      // console.log('🧹 Почистване на картата...');
      
      if (window.map && mapInitializedRef.current) {
        try {
          window.map.setTarget(null);
        } catch (e) {
          console.warn('Грешка при почистване на картата:', e);
        }
      }
      
      // Don't delete window.map completely to allow remounting
      mapInitializedRef.current = false;
    };
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.map && mapInitializedRef.current) {
        window.map.updateSize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          background: '#f5f5f5',
          padding: 3,
          textAlign: 'center',
        }}
      >
        <Box>
          <Typography variant="h5" color="error" gutterBottom>
            ⚠️ Грешка при зареждане на картата
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.location.reload()}
          >
            Опресни страницата
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            zIndex: 1000,
            background: 'white',
            padding: 4,
            borderRadius: 2,
            boxShadow: 3,
          }}
        >
          <Typography variant="h6" gutterBottom>
            {loadingStatus}
          </Typography>
          <CircularProgress size={50} />
        </Box>
      )}

      <Box
        ref={mapContainerRef}
        id="map"
        sx={{
          width: '100%',
          height: '100%',
          padding: 0,
          margin: 0,
          opacity: isLoading ? 0.3 : 1,
          transition: 'opacity 0.3s ease',
        }}
      >
        <div id="popup" className="ol-popup">
          <a href="#" id="popup-closer" className="ol-popup-closer"></a>
          <div id="popup-content"></div>
        </div>
      </Box>
    </Box>
  );
};

export default Karta;
